import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import type {
  DataClassification,
  ClassificationNode,
  CreateClassificationRequest,
  UpdateClassificationRequest,
} from '@/types/classification'

/**
 * Form validation schema
 */
const formSchema = z.object({
  name: z
    .string()
    .min(1, 'Name is required')
    .max(255, 'Name must be less than 255 characters')
    .regex(
      /^[A-Z0-9_]+$/,
      'Name must contain only uppercase letters, numbers, and underscores'
    ),
  displayName: z
    .string()
    .min(1, 'Display name is required')
    .max(255, 'Display name must be less than 255 characters'),
  description: z.string().max(1000).optional(),
  level: z.enum([
    'PUBLIC',
    'INTERNAL',
    'CONFIDENTIAL',
    'RESTRICTED',
    'ITAR',
    'REGULATORY_COMPLIANCE',
    'INTELLECTUAL_PROPERTY',
    'CONTRACTS_AGREEMENTS',
    'PRIVACY_CONSENT',
  ]),
  color: z.string().regex(/^#[0-9A-Fa-f]{6}$/, 'Invalid hex color').optional(),
  regulatoryFrameworks: z.string().optional(),
})

type FormValues = z.infer<typeof formSchema>

interface ClassificationFormDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  classification?: DataClassification | ClassificationNode
  parentClassification?: ClassificationNode
  onSubmit: (
    data: CreateClassificationRequest | UpdateClassificationRequest
  ) => void
  isSubmitting?: boolean
}

/**
 * ClassificationFormDialog provides a dialog for creating or editing classifications
 *
 * Supports:
 * - Create new classification
 * - Edit existing classification
 * - Create child classification (with parent)
 */
export function ClassificationFormDialog({
  open,
  onOpenChange,
  classification,
  parentClassification,
  onSubmit,
  isSubmitting = false,
}: ClassificationFormDialogProps) {
  const isEdit = !!classification
  const isChild = !!parentClassification

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: '',
      displayName: '',
      description: '',
      level: parentClassification?.level || 'INTERNAL',
      color: '',
      regulatoryFrameworks: '',
    },
  })

  // Reset form when classification changes
  useEffect(() => {
    if (classification) {
      form.reset({
        name: classification.name,
        displayName: classification.displayName,
        description: classification.description || '',
        level: classification.level,
        color: classification.color || '',
        regulatoryFrameworks: classification.regulatoryFramework?.join(', ') || '',
      })
    } else {
      form.reset({
        name: '',
        displayName: '',
        description: '',
        level: parentClassification?.level || 'INTERNAL',
        color: '',
        regulatoryFrameworks: '',
      })
    }
  }, [classification, parentClassification, form])

  const handleSubmit = (values: FormValues) => {
    const frameworks = values.regulatoryFrameworks
      ? values.regulatoryFrameworks
          .split(',')
          .map((f) => f.trim())
          .filter(Boolean)
      : []

    const colorValue = values.color && /^#[0-9A-Fa-f]{6}$/.test(values.color)
      ? values.color
      : undefined

    const data = {
      name: values.name,
      displayName: values.displayName,
      description: values.description || undefined,
      level: values.level,
      color: colorValue,
      regulatoryFramework: frameworks.length > 0 ? frameworks : undefined,
      parentId: parentClassification?.id,
    }

    onSubmit(data)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isEdit
              ? 'Edit Classification'
              : isChild
                ? `Create Child of ${parentClassification.displayName}`
                : 'Create Classification'}
          </DialogTitle>
          <DialogDescription>
            {isEdit
              ? 'Update classification details and hierarchy.'
              : 'Create a new data classification for governance and compliance.'}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(handleSubmit)}
            className="space-y-4"
          >
            {/* Name */}
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input placeholder="PII" {...field} disabled={isEdit} />
                  </FormControl>
                  <FormDescription>
                    Unique identifier (uppercase, cannot be changed after
                    creation)
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Display Name */}
            <FormField
              control={form.control}
              name="displayName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Display Name</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Personally Identifiable Information"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Description */}
            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description (Optional)</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Information that can identify an individual..."
                      rows={3}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Level */}
            <FormField
              control={form.control}
              name="level"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Category</FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="REGULATORY_COMPLIANCE">Regulatory Compliance</SelectItem>
                      <SelectItem value="INTELLECTUAL_PROPERTY">Intellectual Property</SelectItem>
                      <SelectItem value="CONTRACTS_AGREEMENTS">Contracts & Agreements</SelectItem>
                      <SelectItem value="PRIVACY_CONSENT">Privacy & Consent</SelectItem>
                      <SelectItem value="PUBLIC">Public</SelectItem>
                      <SelectItem value="INTERNAL">Internal</SelectItem>
                      <SelectItem value="CONFIDENTIAL">Confidential</SelectItem>
                      <SelectItem value="RESTRICTED">Restricted</SelectItem>
                      <SelectItem value="ITAR">ITAR</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Color */}
            <FormField
              control={form.control}
              name="color"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Color (Optional)</FormLabel>
                  <FormControl>
                    <div className="flex gap-2">
                      <Input
                        type="color"
                        className="w-16 h-10 p-1"
                        {...field}
                      />
                      <Input placeholder="#f59e0b" {...field} />
                    </div>
                  </FormControl>
                  <FormDescription>
                    Custom color for this classification (hex format)
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Regulatory Frameworks */}
            <FormField
              control={form.control}
              name="regulatoryFrameworks"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Regulatory Frameworks (Optional)</FormLabel>
                  <FormControl>
                    <Input placeholder="GDPR, CCPA, HIPAA" {...field} />
                  </FormControl>
                  <FormDescription>
                    Comma-separated list of applicable regulations
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting
                  ? isEdit
                    ? 'Updating...'
                    : 'Creating...'
                  : isEdit
                    ? 'Update'
                    : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
